// src/components/StudyHallBoard.jsx
//
// Daily study hall roster and check-in.
//
// Self-contained: it fetches everything through the study hall callables,
// so it works mounted inside the MOPs dashboard today and as its own
// route (/tools/study-hall) for supervisors later, with no changes.
//
// A sanction is counted in SESSIONS SERVED, not days elapsed — a student
// is on this list until they have actually sat the required number of
// study halls. Nothing here advances on its own: if no one checks a
// student in, that student stays on the roster.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

const KA = {
  red: '#E83530',
  navy: '#233C5F',
  gold: '#A29164',
  ink: '#1a1a1a',
  muted: '#6b7280',
  line: '#e5e7eb',
  wash: '#f8f8f7',
  white: '#ffffff',
  green: '#2f7a4d',
};
const BODY = "'Helvetica Neue', Helvetica, Arial, sans-serif";
const DISPLAY = 'Georgia, serif';

const inputStyle = {
  padding: '7px 9px',
  border: `1px solid ${KA.line}`,
  borderRadius: 4,
  fontFamily: BODY,
  fontSize: 13,
  boxSizing: 'border-box',
};

function Btn({ children, onClick, disabled, variant = 'primary', size = 'md', title }) {
  const base = {
    fontFamily: BODY,
    fontWeight: 600,
    borderRadius: 4,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    padding: size === 'sm' ? '5px 10px' : '8px 16px',
    fontSize: size === 'sm' ? 12 : 13,
  };
  const variants = {
    primary: { background: KA.red, color: KA.white, border: 'none' },
    outline: { background: KA.white, color: KA.navy, border: `1px solid ${KA.navy}` },
    ghost: { background: 'transparent', color: KA.muted, border: `1px solid ${KA.line}` },
  };
  return (
    <button title={title} onClick={onClick} disabled={disabled} style={{ ...base, ...variants[variant] }}>
      {children}
    </button>
  );
}

const STATUSES = [
  { key: 'present', label: 'Present', color: KA.green },
  { key: 'absent', label: 'Absent', color: KA.red },
  { key: 'excused', label: 'Excused', color: KA.gold },
];

function todayAmman() {
  return new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);
}

export default function StudyHallBoard({ seasonId, canWrite = false, embedded = false }) {
  const fns = useMemo(() => getFunctions(undefined, 'us-central1'), []);
  const [date, setDate] = useState(todayAmman());
  const [roster, setRoster] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [err, setErr] = useState(null);
  const [notice, setNotice] = useState(null);
  const [blockFilter, setBlockFilter] = useState('');

  // Local edits, flushed on Save. Keyed by personId.
  const [pending, setPending] = useState({});

  const load = useCallback(
    async (d) => {
      setLoading(true);
      setErr(null);
      setPending({});
      try {
        const fn = httpsCallable(fns, 'getStudyHallRoster');
        const res = await fn({ date: d, seasonId });
        setRoster(res.data);
      } catch (e) {
        setErr(e.message);
        setRoster(null);
      } finally {
        setLoading(false);
      }
    },
    [fns, seasonId]
  );

  useEffect(() => {
    load(date);
  }, [date, load]);

  async function save() {
    const entries = Object.entries(pending).map(([personId, v]) => ({
      personId,
      status: v.status,
      sanctionId: v.sanctionId,
      block: v.block,
    }));
    if (!entries.length) return;
    setSaving(true);
    setErr(null);
    setNotice(null);
    try {
      const fn = httpsCallable(fns, 'recordStudyHallAttendance');
      const res = await fn({ date, entries });
      const done = (res.data.updated || []).filter((u) => u.status === 'completed');
      setNotice(
        `Saved ${entries.length} check-in${entries.length === 1 ? '' : 's'}.` +
          (done.length
            ? ` ${done.length} student${done.length === 1 ? '' : 's'} finished their sanction.`
            : '')
      );
      await load(date);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function sync() {
    setSyncing(true);
    setErr(null);
    setNotice(null);
    try {
      const fn = httpsCallable(fns, 'syncStudyHallSanctions');
      const res = await fn({ seasonId });
      const d = res.data || {};
      setNotice(
        `Sanctions synced — ${d.created || 0} new, ${d.superseded || 0} superseded by a higher rung.`
      );
      await load(date);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSyncing(false);
    }
  }

  function setStatus(row, status) {
    setPending((p) => {
      const next = { ...p };
      if (next[row.personId] && next[row.personId].status === status) delete next[row.personId];
      else next[row.personId] = { status, sanctionId: row.sanctionId, block: row.freeBlock };
      return next;
    });
  }

  function markBlockPresent(rows) {
    setPending((p) => {
      const next = { ...p };
      rows.forEach((r) => {
        next[r.personId] = { status: 'present', sanctionId: r.sanctionId, block: r.freeBlock };
      });
      return next;
    });
  }

  function exportCsv() {
    if (!roster) return;
    const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const head = ['Block', 'Time', 'Student', 'SIS ID', 'Grade', 'Advisor', 'Rung', 'Served', 'Required', 'Remaining', 'Attendance'];
    const body = roster.rows.map((r) =>
      [
        r.freeBlock || 'NO FREE BLOCK',
        r.blockTime || '',
        r.person,
        r.personId,
        r.grade,
        r.advisorName,
        `${r.rung} strikes`,
        r.sessionsServed,
        r.sessionsRequired,
        r.remaining,
        (pending[r.personId] && pending[r.personId].status) || r.attendance || '',
      ]
        .map(esc)
        .join(',')
    );
    const blob = new Blob([[head.map(esc).join(','), ...body].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `study-hall-${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const groups = useMemo(() => {
    if (!roster) return [];
    const keys = Object.keys(roster.byBlock || {})
      .filter((k) => k !== 'none')
      .sort();
    const out = keys.map((k) => ({ block: k, rows: roster.byBlock[k] }));
    if (roster.byBlock && roster.byBlock.none) out.push({ block: 'none', rows: roster.byBlock.none });
    return out.filter((g) => !blockFilter || g.block === blockFilter);
  }, [roster, blockFilter]);

  const pendingCount = Object.keys(pending).length;

  const Stat = ({ label, value, tone }) => (
    <div style={{ marginRight: 20 }}>
      <div style={{ fontSize: 21, fontFamily: DISPLAY, color: tone || KA.navy }}>{value}</div>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em', color: KA.muted }}>
        {label}
      </div>
    </div>
  );

  return (
    <div style={{ fontFamily: BODY, color: KA.ink, padding: embedded ? 0 : 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: DISPLAY, color: KA.navy, margin: 0, fontWeight: 400, fontSize: 24 }}>
            Study hall
          </h2>
          <div style={{ fontSize: 12, color: KA.muted, marginTop: 3 }}>
            Students serving a phone-strike sanction during their free block
            {roster && roster.dayType ? ` · ${roster.dayType}` : ''}
            {roster && roster.isExam ? ' · exam day' : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ ...inputStyle, width: 155 }}
          />
          <select value={blockFilter} onChange={(e) => setBlockFilter(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
            <option value="">All blocks</option>
            {(roster ? Object.keys(roster.byBlock || {}) : []).sort().map((b) => (
              <option key={b} value={b}>
                {b === 'none' ? 'No free block' : `Block ${b}`}
              </option>
            ))}
          </select>
          <Btn variant="ghost" size="sm" onClick={exportCsv} disabled={!roster || !roster.rows.length}>
            Export CSV
          </Btn>
          {canWrite && (
            <Btn variant="outline" size="sm" onClick={sync} disabled={syncing} title="Create sanctions for anyone newly over a threshold">
              {syncing ? 'Syncing…' : 'Sync sanctions'}
            </Btn>
          )}
        </div>
      </div>

      {notice && (
        <div style={{ marginTop: 14, padding: '10px 14px', background: '#f2f6ee', borderLeft: `3px solid ${KA.gold}`, fontSize: 13 }}>
          {notice}
        </div>
      )}
      {err && (
        <div style={{ marginTop: 14, padding: '10px 14px', background: '#fdf0ef', borderLeft: `3px solid ${KA.red}`, fontSize: 13 }}>
          {err}
        </div>
      )}

      {loading && <div style={{ padding: 30, color: KA.muted, fontSize: 13 }}>Loading roster…</div>}

      {roster && !loading && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', margin: '16px 0 10px', paddingBottom: 10, borderBottom: `1px solid ${KA.line}` }}>
            <Stat label="On sanction" value={roster.total} />
            <Stat label="Block meets today" value={roster.dueToday} />
            <Stat label="Recorded" value={roster.recorded} />
            <Stat label="Present" value={roster.present} tone={KA.green} />
            {roster.noFreeBlock > 0 && <Stat label="No free block" value={roster.noFreeBlock} tone={KA.red} />}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
              {pendingCount > 0 && (
                <span style={{ fontSize: 12, color: KA.navy, fontWeight: 600 }}>
                  {pendingCount} unsaved
                </span>
              )}
              <Btn onClick={save} disabled={!pendingCount || saving || !canWrite}>
                {saving ? 'Saving…' : 'Save check-in'}
              </Btn>
            </div>
          </div>

          {!roster.isSchoolDay && (
            <div style={{ padding: '8px 12px', background: KA.wash, borderLeft: `3px solid ${KA.gold}`, fontSize: 12, marginBottom: 12 }}>
              {date} is not a class day according to the schedule. Anything recorded here still
              counts toward a sanction, so only check students in if study hall actually ran.
            </div>
          )}
          {!roster.scheduleKnown && (
            <div style={{ padding: '8px 12px', background: KA.wash, borderLeft: `3px solid ${KA.gold}`, fontSize: 12, marginBottom: 12 }}>
              No <code>/dailySchedule/{date}</code> entry, so every block is shown. Check in only the
              students whose block actually met.
            </div>
          )}

          {!roster.rows.length && (
            <div style={{ padding: 36, textAlign: 'center', color: KA.muted, fontSize: 13, border: `1px dashed ${KA.line}`, borderRadius: 6 }}>
              Nobody is serving a study hall sanction on this date.
            </div>
          )}

          {groups.map((g) => {
            const isNone = g.block === 'none';
            const meets = g.rows.length && g.rows[0].blockMeetsToday;
            return (
              <div key={g.block} style={{ marginTop: 16 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '7px 11px',
                    background: isNone ? '#fdf0ef' : KA.wash,
                    borderLeft: `3px solid ${isNone ? KA.red : meets ? KA.navy : KA.line}`,
                  }}
                >
                  <strong style={{ fontSize: 14, color: isNone ? KA.red : KA.navy }}>
                    {isNone ? 'No free block' : `Block ${g.block}`}
                  </strong>
                  {g.rows[0] && g.rows[0].blockTime && (
                    <span style={{ fontSize: 12, color: KA.navy, fontWeight: 600 }}>
                      {g.rows[0].blockTime}
                    </span>
                  )}
                  <span style={{ fontSize: 12, color: KA.muted }}>
                    {g.rows.length} student{g.rows.length === 1 ? '' : 's'}
                  </span>
                  {isNone ? (
                    <span style={{ fontSize: 11, color: KA.red }}>
                      — no free period to convert; needs a different consequence
                    </span>
                  ) : (
                    !meets &&
                    roster.scheduleKnown && (
                      <span style={{ fontSize: 11, color: KA.muted }}>— does not meet today</span>
                    )
                  )}
                  {canWrite && !isNone && (
                    <Btn
                      variant="ghost"
                      size="sm"
                      onClick={() => markBlockPresent(g.rows)}
                      title="Mark everyone in this block present"
                    >
                      All present
                    </Btn>
                  )}
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {g.rows.map((r, i) => {
                      const chosen = (pending[r.personId] && pending[r.personId].status) || r.attendance;
                      const isPending = !!pending[r.personId];
                      return (
                        <tr key={r.personId} style={{ borderBottom: `1px solid ${KA.line}`, background: isPending ? '#f4f7fb' : 'transparent' }}>
                          <td style={{ padding: '8px 10px', width: 26, color: KA.muted, fontVariantNumeric: 'tabular-nums' }}>
                            {i + 1}
                          </td>
                          <td style={{ padding: '8px 10px', fontWeight: 600, color: KA.navy }}>
                            {r.person || `Student ${r.personId}`}
                          </td>
                          <td style={{ padding: '8px 10px', color: KA.muted }}>{r.personId}</td>
                          <td style={{ padding: '8px 10px', color: KA.muted }}>
                            {r.grade != null ? `Gr ${r.grade}` : '—'}
                          </td>
                          <td style={{ padding: '8px 10px', color: KA.muted, fontSize: 12 }}>
                            {r.rung} strikes
                          </td>
                          <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                            <strong style={{ color: KA.navy }}>
                              {r.sessionsServed}/{r.sessionsRequired}
                            </strong>
                            <span style={{ color: KA.muted, fontSize: 11, marginLeft: 6 }}>
                              {r.remaining} left
                            </span>
                          </td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {STATUSES.map((s) => {
                              const on = chosen === s.key;
                              return (
                                <button
                                  key={s.key}
                                  disabled={!canWrite}
                                  onClick={() => setStatus(r, s.key)}
                                  style={{
                                    marginLeft: 5,
                                    padding: '4px 10px',
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    borderRadius: 4,
                                    cursor: canWrite ? 'pointer' : 'default',
                                    border: `1px solid ${on ? s.color : KA.line}`,
                                    background: on ? s.color : KA.white,
                                    color: on ? KA.white : KA.muted,
                                  }}
                                >
                                  {s.label}
                                </button>
                              );
                            })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}

          <div style={{ fontSize: 11, color: KA.muted, marginTop: 20, borderTop: `1px solid ${KA.line}`, paddingTop: 10 }}>
            Only <strong>Present</strong> counts down a sanction. A student stays on this roster until
            they have served every session, however long that takes — absences and holidays simply
            extend it rather than letting it expire.
          </div>
        </>
      )}
    </div>
  );
}